/**
 * How long a toast stays on screen, by what kind of news it is (#1137).
 *
 * ── The complaint ─────────────────────────────────────────────────────────
 *
 * Darcy reported this three ways on 8/4 — "error message disappears before I
 * can read it", "disappears too fast to read" — while trying to attach
 * documents to an invoice. The worker knows something failed but not what,
 * which turns a fixable error into a mystery.
 *
 * The cause is an omission, not a bug: `ui/toaster.tsx` set `richColors` and
 * `closeButton` but no `duration`, so Sonner's **4-second default** governed
 * every one of the 718 `toast.error` call sites in the client.
 *
 * ── Why this is a patch rather than a Toaster prop ────────────────────────
 *
 * Sonner 2.x exposes ONE global `duration` on `<Toaster>` and no per-type
 * override. Raising the global would also make 595 success toasts linger,
 * which is its own annoyance — a "Saved" that sits there for fifteen seconds
 * reads as though something is still happening.
 *
 * The alternatives were worse: editing 718 call sites, or wrapping `toast` in
 * a local module and rewriting every import. Defaulting the duration on the
 * two failure methods, once, is the smallest change that fixes the whole
 * class — and it is a DEFAULT, so any call site that already passes its own
 * `duration` still wins (e.g. close-out's 14s partial-delivery toast).
 *
 * Applied by `ui/toaster.tsx`, which is imported once from `main.tsx`.
 */
import { toast } from "sonner";

/**
 * Failure toasts. Long enough to read a sentence, look away, and look back —
 * but not sticky: several stacked un-dismissable errors are their own problem,
 * and the close button is already there for anyone who wants it gone sooner.
 */
export const ERROR_TOAST_MS = 15_000;

/** Warnings are "you should know", not "this failed". Shorter than an error. */
export const WARNING_TOAST_MS = 8_000;

/**
 * Success toasts keep Sonner's 4s default deliberately — the work is done and
 * the screen already shows it. This constant exists to make that a stated
 * decision rather than an omission, which is what caused #1137.
 */
export const SUCCESS_TOAST_MS = 4_000;

type ToastFn = typeof toast.error;

/**
 * Wrap one toast method so it carries `ms` unless the caller chose otherwise.
 *
 * Exported so it can be tested DIRECTLY. Testing it through
 * `applyToastDurations` instead looks equivalent and is not: that function is
 * idempotent, so a spy installed after the first call is never wrapped and the
 * assertion passes on the caller's own arguments — green, and proving nothing.
 */
export function withDefaultDuration(fn: ToastFn, ms: number): ToastFn {
    return ((message: Parameters<ToastFn>[0], data?: Parameters<ToastFn>[1]) =>
        fn(message, { duration: ms, ...(data ?? {}) })) as ToastFn;
}

let applied = false;

/**
 * Give `toast.error` and `toast.warning` their default durations.
 *
 * Idempotent: calling twice must not double-wrap, or a second mount in a test
 * would nest the wrappers and make the spread order meaningless.
 */
export function applyToastDurations(): void {
    if (applied) return;
    applied = true;
    toast.error = withDefaultDuration(toast.error, ERROR_TOAST_MS);
    toast.warning = withDefaultDuration(toast.warning, WARNING_TOAST_MS);
}

/** Test-only: forget that the patch was applied. Never called by the app. */
export function resetToastDurationsForTest(): void {
    applied = false;
}
